import { Box, Flex, Heading, SimpleGrid, Text, VStack, Wrap, WrapItem, useBreakpointValue } from "@chakra-ui/react";
import { motion, useReducedMotion, useScroll, useSpring, useTransform, type MotionValue } from "framer-motion";
import { useRef } from "react";
import { portfolio, type DeliveryStage } from "../data/portfolio";
import { MotionFade } from "./MotionFade";
import { Accent, SectionHeading } from "./SectionHeading";

const MotionBox = motion(Box);

const ACCENT = "#e8a44a";
const IDLE = "rgba(255,255,255,0.22)";

type StageProps = {
  stage: DeliveryStage;
  index: number;
  total: number;
  progress: MotionValue<number>;
};

function Stage({ stage, index, total, progress }: StageProps) {
  const at = index / total;
  const opacity = useTransform(progress, [at - 0.1, at + 0.03], [0.32, 1]);
  const nodeColor = useTransform(progress, [at - 0.04, at + 0.03], [IDLE, ACCENT]);
  const nodeScale = useTransform(progress, [at - 0.04, at + 0.03], [1, 1.35]);

  return (
    <Box position="relative" pl={{ base: 8, lg: 0 }} pr={{ lg: 8 }} pt={{ lg: 10 }} pb={{ base: 12, lg: 0 }}>
      <MotionBox
        position="absolute"
        left={{ base: "-5px", lg: 0 }}
        top={{ base: "6px", lg: "-5px" }}
        w="11px"
        h="11px"
        borderRadius="full"
        boxShadow="0 0 0 5px #141419"
        style={{ backgroundColor: nodeColor, scale: nodeScale }}
      />
      <MotionBox style={{ opacity }}>
        <Text fontFamily="mono" fontSize="xs" letterSpacing="0.14em" color="accent.300">
          {String(index + 1).padStart(2, "0")}
        </Text>
        <Heading as="h3" fontSize={{ base: "2xl", lg: "1.75rem" }} letterSpacing="-0.02em" color="white" mt={2}>
          {stage.title}
        </Heading>
        <Text mt={3} color="graphite.400" lineHeight="tall" minH={{ lg: "7.5em" }}>
          {stage.text}
        </Text>
        <VStack as="ul" align="stretch" spacing={0} mt={5} listStyleType="none">
          {stage.skills.map((skill) => (
            <Flex
              as="li"
              key={skill}
              align="center"
              gap={3}
              py={2.5}
              borderTopWidth="1px"
              borderColor="whiteAlpha.100"
              color="graphite.100"
              fontWeight="500"
              transition="color 0.2s ease, padding-left 0.25s ease"
              _hover={{ color: "accent.300", pl: 1.5 }}
            >
              <Box w="5px" h="5px" borderRadius="full" bg="accent.400" flexShrink={0} />
              {skill}
            </Flex>
          ))}
        </VStack>
      </MotionBox>
    </Box>
  );
}

export function DeliverySection() {
  const { stages, domain } = portfolio.delivery;
  const trackRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const isHorizontal = useBreakpointValue({ base: false, lg: true }, { ssr: false }) ?? false;

  const { scrollYProgress } = useScroll({ target: trackRef, offset: ["start 0.85", "end 0.55"] });
  const smooth = useSpring(scrollYProgress, { stiffness: 90, damping: 22, mass: 0.4 });
  const full = useTransform(smooth, () => 1);
  const progress = prefersReducedMotion ? full : smooth;
  const pulseOffset = useTransform(progress, (v) => `${Math.min(v, 1) * 100}%`);

  return (
    <Box id="skills" as="section" py={{ base: 20, md: 32 }} px={{ base: 5, md: 8 }} scrollMarginTop="72px">
      <Box maxW="container.xl" mx="auto">
        <SectionHeading
          section="skills"
          title={
            <>
              Do commit à loja, <Accent>com rastro</Accent> em cada etapa
            </>
          }
          lead="Minhas competências na ordem em que entram em um release: o que sustenta o app, o que garante que ele funciona, o que o coloca nas lojas e o que o mantém saudável depois."
        />

        <Box ref={trackRef} position="relative" mt={{ lg: 6 }}>
          <Box
            position="absolute"
            left={0}
            top={0}
            bottom={{ base: 0, lg: "auto" }}
            right={{ base: "auto", lg: 0 }}
            w={{ base: "1px", lg: "auto" }}
            h={{ base: "auto", lg: "1px" }}
            bg="whiteAlpha.200"
          >
            <MotionBox
              position="absolute"
              inset={0}
              bg="accent.400"
              style={
                isHorizontal
                  ? { scaleX: progress, transformOrigin: "left center" }
                  : { scaleY: progress, transformOrigin: "center top" }
              }
            />
            {!prefersReducedMotion ? (
              <MotionBox
                position="absolute"
                w="9px"
                h="9px"
                borderRadius="full"
                bg="accent.200"
                boxShadow="0 0 18px 6px rgba(232,164,74,0.55)"
                transform="translate(-50%, -50%)"
                style={isHorizontal ? { left: pulseOffset, top: "50%" } : { top: pulseOffset, left: "50%" }}
              />
            ) : null}
          </Box>

          <SimpleGrid columns={{ base: 1, lg: stages.length }} spacing={0}>
            {stages.map((stage, i) => (
              <Stage key={stage.title} stage={stage} index={i} total={stages.length} progress={progress} />
            ))}
          </SimpleGrid>
        </Box>

        <MotionFade mt={{ base: 4, lg: 20 }}>
          <Flex
            direction={{ base: "column", lg: "row" }}
            gap={{ base: 6, lg: 12 }}
            align={{ lg: "center" }}
            justify="space-between"
            p={{ base: 6, md: 8 }}
            borderRadius="2xl"
            borderWidth="1px"
            borderColor="whiteAlpha.200"
            bg="whiteAlpha.50"
          >
            <Box maxW="md">
              <Text fontFamily="mono" fontSize="xs" letterSpacing="0.14em" textTransform="uppercase" color="accent.300">
                Domínio
              </Text>
              <Heading as="h3" fontSize="xl" color="white" mt={2}>
                {domain.title}
              </Heading>
              <Text mt={2} color="graphite.400">
                {domain.text}
              </Text>
            </Box>
            <Wrap spacing={2} justify={{ lg: "flex-end" }} flex={1}>
              {domain.skills.map((skill) => (
                <WrapItem key={skill}>
                  <Text
                    px={4}
                    py={2}
                    borderRadius="full"
                    borderWidth="1px"
                    borderColor="whiteAlpha.300"
                    color="graphite.100"
                    fontSize="sm"
                    fontWeight="500"
                    transition="border-color 0.2s ease, color 0.2s ease"
                    _hover={{ borderColor: "accent.400", color: "accent.300" }}
                  >
                    {skill}
                  </Text>
                </WrapItem>
              ))}
            </Wrap>
          </Flex>
        </MotionFade>
      </Box>
    </Box>
  );
}
